import { useQuery } from '@tanstack/react-query';
import { endOfMonth, format, subMonths } from 'date-fns';
import { nps } from '@/shared/api/client';
import type {
  AnswerDataPoint,
  AnswerMonthlyTotals,
  AnswerRanking,
  ContentI18n,
  IntegrationEvent,
  IntegrationProvider,
  NpsIntegration,
  NpsQuestion,
  NpsSurvey,
  NpsUser,
  NpsUserPreference,
  QuestionMetadata,
  QuestionResponseType,
  SurveyExpiration,
  SurveyRanking,
  SurveyResponseType,
  SurveyStatus,
  NpsRole,
} from '@/shared/types/nps';
import type { CreateIntegrationPayload } from '../schemas/integration.schema';
import { npsFetchBlob, npsUpload } from '../lib/nps-upload';

/* ──────────────── surveys ──────────────── */

export interface SurveyPayload {
  title: string;
  description?: string;
  i18n?: ContentI18n;
  response_type?: SurveyResponseType;
  response_expiration_time?: SurveyExpiration;
  identification_required?: boolean;
  answer_bonus?: boolean;
  /** Admin-only: create for another NPS user. */
  user_id?: string;
}

export function useSurveys(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['nps', 'surveys'],
    queryFn: () => nps.get<NpsSurvey[]>('/surveys'),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

export function useSurvey(id: string | undefined) {
  return useQuery({
    queryKey: ['nps', 'survey', id],
    // Admin edita canônico (pt-BR): sem isto o Accept-Language do browser
    // (ex.: `pt` → pt-PT) projeta traduções em title/description e corrompe o form.
    queryFn: () => nps.get<NpsSurvey>(`/surveys/${id}?lang=pt-BR`),
    enabled: Boolean(id),
  });
}

export const createSurvey = (data: SurveyPayload) => nps.post<NpsSurvey>('/surveys', data);
export const updateSurvey = (id: string, data: Partial<SurveyPayload>) => nps.patch<NpsSurvey>(`/surveys/${id}`, data);
export const updateSurveyStatus = (id: string, status: SurveyStatus) => nps.put<NpsSurvey>(`/surveys/${id}`, { status });

/* ──────────────── questions ──────────────── */

export interface QuestionPayload {
  title: string;
  description?: string;
  i18n?: ContentI18n;
  required?: boolean;
  response_type: QuestionResponseType;
  response_metadata?: QuestionMetadata;
}

export const createQuestion = (surveyId: string, data: QuestionPayload) =>
  nps.post<NpsQuestion>(`/questions/${surveyId}`, data);
export const updateQuestion = (id: string, data: Partial<QuestionPayload>) =>
  nps.patch<NpsQuestion>(`/questions/${id}`, data);
export const removeQuestion = (id: string) => nps.delete(`/questions/${id}`);
export const reorderQuestions = (surveyId: string, items: Array<{ id: string; position: number }>) =>
  nps.patch<NpsQuestion[]>(`/questions/survey/${surveyId}/positions`, { items });

/* ──────────────── answers / dashboard ──────────────── */

function defaultAnswerRange() {
  const endDate = endOfMonth(new Date());
  const startDate = endOfMonth(subMonths(endDate, 5));
  return { from: format(startDate, 'yyyy-MM-dd'), to: format(endDate, 'yyyy-MM-dd') };
}

export function useAnswerMonthlyTotals(surveyIds?: string[]) {
  const range = defaultAnswerRange();
  const idsKey = surveyIds?.length ? [...surveyIds].sort().join(',') : 'all';
  const qs = new URLSearchParams({
    from: range.from,
    to: range.to,
    range_type: 'monthly',
  });
  if (surveyIds?.length) qs.set('survey_ids', surveyIds.join(','));
  return useQuery({
    queryKey: ['nps', 'answers', 'monthly', range.from, range.to, idsKey],
    queryFn: () => nps.get<AnswerMonthlyTotals>(`/answers/data?${qs.toString()}`),
    staleTime: 60_000,
    enabled: surveyIds === undefined || surveyIds.length > 0,
  });
}

/** Série enriquecida para gráfico empilhado (derivada do mock ou futuro endpoint). */
export function useAnswerData(surveyIds?: string[]) {
  const idsKey = surveyIds?.length ? [...surveyIds].sort().join(',') : 'all';
  const qs = surveyIds?.length ? `?survey_ids=${encodeURIComponent(surveyIds.join(','))}` : '';
  return useQuery({
    queryKey: ['nps', 'answers', 'data', idsKey],
    queryFn: () => nps.get<AnswerDataPoint[]>(`/answers/data/enriched${qs}`),
    staleTime: 60_000,
    enabled: surveyIds === undefined || surveyIds.length > 0,
  });
}

export function useSurveyRanking() {
  return useQuery({
    queryKey: ['nps', 'answers', 'ranking', 'surveys'],
    queryFn: () => nps.get<SurveyRanking[]>('/answers/ranking/surveys'),
    staleTime: 60_000,
  });
}

export function useOrgRanking(enabled: boolean) {
  return useQuery({
    queryKey: ['nps', 'answers', 'ranking', 'org'],
    queryFn: () => nps.get<AnswerRanking[]>('/answers/ranking'),
    enabled,
    staleTime: 60_000,
  });
}

/** @deprecated Use useSurveyRanking */
export function useRanking() {
  return useSurveyRanking();
}

export const getPublicSurvey = (id: string, lang?: string) => {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  return nps.get<NpsSurvey>(`/surveys/${id}${qs}`, {
    headers: lang ? { 'Accept-Language': lang } : undefined,
  });
};
export const submitAnswer = (
  surveyId: string,
  payload: { identifier?: string; answers: Array<{ question_id: string; value: unknown }> },
  lang?: string,
) => {
  const qs = lang ? `?lang=${encodeURIComponent(lang)}` : '';
  return nps.post(`/answers/${surveyId}${qs}`, payload, {
    headers: lang ? { 'Accept-Language': lang } : undefined,
  });
};

/* ──────────────── users ──────────────── */

export interface NpsUserPayload {
  name: string;
  last_name?: string;
  email: string;
  password?: string;
  tax_identifier?: string;
  role?: NpsRole;
  legacy_user_id?: string;
}

export function useNpsUsers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['nps', 'users'],
    queryFn: () => nps.get<NpsUser[]>('/users'),
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  });
}

export function useNpsUser(id: string | undefined) {
  return useQuery({
    queryKey: ['nps', 'users', id],
    queryFn: () => nps.get<NpsUser>(`/users/${id}`),
    enabled: Boolean(id),
  });
}

export const createNpsUser = (data: NpsUserPayload) => nps.post<NpsUser>('/users', data);
export const updateNpsUser = (id: string, data: Partial<NpsUserPayload>) => nps.patch<NpsUser>(`/users/${id}`, data);
export const deleteNpsUser = (id: string) => nps.delete(`/users/${id}`);

export function useNpsMe(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['nps', 'me'],
    queryFn: () => nps.get<NpsUser>('/users/me'),
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  });
}

export const updateNpsAccount = (data: {
  name?: string;
  last_name?: string;
  tax_identifier?: string;
  legacy_user_id?: string;
}) => nps.patch<NpsUser>('/users/me', data);

export const updateNpsPreferences = (formData: FormData) =>
  npsUpload<NpsUserPreference>('/user-preferences', formData);

export const uploadNpsBanner = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return npsUpload<Blob>('/users/me/banner', fd);
};

export const uploadNpsProfilePhoto = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return npsUpload<Blob>('/users/me/profile', fd);
};

export const fetchNpsBannerBlob = () => npsFetchBlob('/users/me/banner');
export const fetchNpsProfileBlob = () => npsFetchBlob('/users/me/profile');

/* ──────────────── integrations ──────────────── */

export function useIntegrations() {
  return useQuery({ queryKey: ['nps', 'integrations'], queryFn: () => nps.get<NpsIntegration[]>('/integrations'), staleTime: 30_000 });
}
export function useAvailableProviders() {
  return useQuery({ queryKey: ['nps', 'providers'], queryFn: () => nps.get<IntegrationProvider[]>('/integrations/available'), staleTime: 5 * 60_000 });
}
export function useIntegration(id: string | undefined) {
  return useQuery({ queryKey: ['nps', 'integration', id], queryFn: () => nps.get<NpsIntegration>(`/integrations/${id}`), enabled: Boolean(id) });
}
export function useIntegrationEvents(id: string | undefined) {
  return useQuery({ queryKey: ['nps', 'integration-events', id], queryFn: () => nps.get<IntegrationEvent[]>(`/integration-webhook-logs/${id}`), enabled: Boolean(id) });
}

export const createIntegration = (data: CreateIntegrationPayload) => nps.post<NpsIntegration>('/integrations', data);

export const authShopify = (storeName: string) =>
  nps.post<{ url: string }>('/integrations/shopify/auth', { store_name: storeName });

export const createWebhook = (data: { integration_id: string; type: 'ORDER_PAID' | 'UNINSTALLED' }) =>
  nps.post('/integrations/webhooks', data);
export const deleteWebhook = (integrationId: string, id: string) =>
  nps.delete(`/integrations/webhooks/${integrationId}/${id}`);
